import React, { useState } from 'react'
import Box from '@mui/material/Box'
import { EmtTypography, EmtButton } from 'emt-components-v2'
import { useSignUpCallRequestStore } from '@/store/signUpCallRequestStore'
import AnimalForm, { AnimalData } from '@/components/PetSitter/AddAnimal/AnimalForm'
import AnimalBox from '@/components/PetSitter/AnimalBox'
import AddBox from '@/components/PetSitter/AddBox'
import useIsResponsive from '@/hooks/useIsResponsive'

interface PetsStepComponentProps {
  onNext: () => void
}

const PetsStep: React.FC<PetsStepComponentProps> = ({ onNext }) => {
  const [isMobile] = useIsResponsive()

  const pets = useSignUpCallRequestStore((s) => s.animals)
  const addAnimal = useSignUpCallRequestStore((s) => s.addAnimal)
  const updateAnimal = useSignUpCallRequestStore((s) => s.updateAnimal)
  const removeAnimal = useSignUpCallRequestStore((s) => s.removeAnimal)
  const [showForm, setShowForm] = useState<boolean>(pets.length === 0)
  const [selectedPet, setSelectedPet] = useState<AnimalData | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Add or edit pet
  const handleFormSubmit = (values: AnimalData) => {
    setIsLoading(true)
    if (selectedPet) {
      updateAnimal({ ...values, uuid: selectedPet.uuid })
    } else {
      const uuid = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      addAnimal({ ...values, uuid })
    }
    setShowForm(false)
    setSelectedPet(null)
    setIsLoading(false)
  }

  // Edit pet
  const editAnimal = (uuid: string) => {
    const petToEdit = pets.find((p) => p.uuid === uuid)
    if (!petToEdit) return
    setSelectedPet(petToEdit)
    setShowForm(true)
  }

  // Delete pet
  const deleteAnimal = (uuid: string) => {
    removeAnimal(uuid)
    setShowForm(false)
    setSelectedPet(null)
  }

  // Add new pet
  const handleAddPet = () => {
    setSelectedPet(null)
    setShowForm(true)
  }

  // Continue button enabled if at least one pet
  const canContinue = pets.length > 0

  const handleContinue = () => {
    onNext()
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
      <Box>
        <EmtTypography variant="h3" fontWeight="800" sx={{ fontSize: isMobile ? '24px' : '34px' }}>
          Pour quels animaux ?
        </EmtTypography>
      </Box>

      <EmtTypography variant="body1" fontWeight="700">
        Pour trouver le pet sitter qui correspondra parfaitement à vos besoins, nous avons besoin de quelques
        informations à propos de vos animaux.
      </EmtTypography>

      {/* List of pets */}
      {pets.map((pet) =>
        showForm && selectedPet && pet.uuid === selectedPet.uuid ? (
          <Box key={pet.uuid} sx={{ backgroundColor: '#fff', p: '25px', borderRadius: '16px' }}>
            <AnimalForm
              initialValues={pet}
              onSubmit={handleFormSubmit}
              onCancel={() => {
                setShowForm(false)
                setSelectedPet(null)
              }}
              isLoading={isLoading}
              color="purple"
              canCancel={pets.length > 0}
              variant="signup"
              defaultImages
              defaultDescription={
                "La description de cet animal n'a pas été donnée par son propriétaire. Si vous en avez besoin, vous pouvez l'encourager à vous donner une description via la messagerie."
              }
              showVolunteerSection={false}
            />
          </Box>
        ) : (
          <AnimalBox key={pet.uuid} data={pet} color="purple" edit={editAnimal} delete={deleteAnimal} />
        ),
      )}

      {/* Add new pet box */}
      {(!showForm || (selectedPet && !pets.some((pet) => pet.uuid === selectedPet.uuid))) && (
        <AddBox handleAddPet={handleAddPet} />
      )}

      {/* New pet form */}
      {showForm && !selectedPet && (
        <Box sx={{ backgroundColor: '#fff', p: '25px', borderRadius: '16px' }}>
          <AnimalForm
            key="new"
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setShowForm(false)
              setSelectedPet(null)
            }}
            isLoading={isLoading}
            color="purple"
            canCancel={pets.length > 0}
            variant="signup"
            defaultImages
            defaultDescription={
              "La description de cet animal n'a pas été donnée par son propriétaire. Si vous en avez besoin, vous pouvez l'encourager à vous donner une description via la messagerie."
            }
            showVolunteerSection={false}
          />
        </Box>
      )}

      {!showForm && (
        <EmtButton
          fullWidth
          color="purple"
          size="large"
          variant="contained"
          disabled={!canContinue}
          onClick={handleContinue}
        >
          Continuer
        </EmtButton>
      )}
    </Box>
  )
}

export default PetsStep
